import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Player, LeagueDetail, LeagueUpdateInput, AddPlayersToLeagueRequest } from '../generated-api';
import { DefaultService } from '../generated-api';
import SimplePlayerSelectionGrid from '../components/SimplePlayerSelectionGrid';
import LeagueInfoFields from '../components/LeagueInfoFields';
import { useLeagues } from '../contexts/DataContext';

const LeagueEditPage: React.FC = () => {
  const { id: leagueId } = useParams<{ id: string }>();
  const { invalidate: invalidateLeagues } = useLeagues();
  const [league, setLeague] = useState<LeagueDetail | null>(null);
  const [leagueName, setLeagueName] = useState('');
  const [leagueIcon, setLeagueIcon] = useState('');
  const [leagueDescription, setLeagueDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set());
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  // No need to instantiate DefaultService - it's a static service

  const fetchLeagueAndPlayers = useCallback(async () => {
    if (!leagueId) return;

    try {
      setInitialLoading(true);
      
      // Fetch league details and all players in parallel
      const [leagueResponse, playersResponse] = await Promise.all([
        DefaultService.getLeagueLeaguesLeagueIdGet({ leagueId }),
        DefaultService.getPlayersPlayersGet()
      ]);

      const leagueData = leagueResponse;
      if (leagueData) {
        setLeague(leagueData);
        setLeagueName(leagueData.name);
        setLeagueIcon(leagueData.icon || '');
        setLeagueDescription(leagueData.description || '');
        setIsPublic(leagueData.isPublic ?? true);
        setSelectedPlayers(new Set(leagueData.playerIds || []));
      }

      setAllPlayers(playersResponse || []);
    } catch (err) {
      setError('Failed to fetch league details');
      console.error('Error fetching league:', err);
    } finally {
      setInitialLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (leagueId) {
      fetchLeagueAndPlayers();
    }
  }, [leagueId, fetchLeagueAndPlayers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!league || league.isGlobal) {
      setError('Cannot edit the Global league');
      return;
    }

    if (!leagueName.trim()) {
      setError('League name is required');
      return;
    }

    if (selectedPlayers.size === 0) {
      setError('Please select at least one player for the league');
      return;
    }

    try {
      setLoading(true);

      // Update league basic information
      const leagueUpdate: LeagueUpdateInput = {
        name: leagueName.trim(),
        icon: leagueIcon.trim() || undefined,
        description: leagueDescription.trim() || undefined,
        isPublic: isPublic
      };

      await DefaultService.updateLeagueLeaguesLeagueIdPut({ leagueId: league.id, requestBody: leagueUpdate });

      // Update league membership
      const currentPlayerIds = new Set(league.playerIds || []);
      const newPlayerIds = selectedPlayers;

      // Players to add
      const playersToAdd = Array.from(newPlayerIds).filter(id => !currentPlayerIds.has(id));
      if (playersToAdd.length > 0) {
        const addRequest: AddPlayersToLeagueRequest = { playerIds: playersToAdd };
        await DefaultService.addPlayersToLeagueLeaguesLeagueIdPlayersPost({ leagueId: league.id, requestBody: addRequest });
      }

      // Players to remove
      const playersToRemove = Array.from(currentPlayerIds).filter(id => !newPlayerIds.has(id));
      for (const playerId of playersToRemove) {
        await DefaultService.removePlayerFromLeagueLeaguesLeagueIdPlayersPlayerIdDelete({ leagueId: league.id, playerId });
      }

      // Invalidate leagues cache to ensure fresh data on next load
      invalidateLeagues();
      navigate(`/leagues/${league.id}`);
    } catch (err) {
      console.error('Error updating league:', err);
      setError('Failed to update league. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading league details...</p>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          League not found
        </div>
        <Link to="/leagues" className="text-primary-500 hover:text-primary-700">
          ← Back to Leagues
        </Link>
      </div>
    );
  }

  if (league.isGlobal) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          The Global league cannot be edited.
        </div>
        <Link to="/leagues" className="text-primary-500 hover:text-primary-700">
          ← Back to Leagues
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link to={`/leagues/${league.id}`} className="text-primary-500 hover:text-primary-700 mb-4 inline-block">
          ← Back to League
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Edit League</h1>
        <p className="mt-2 text-gray-600">Update league information and manage membership</p>
      </div>

      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic League Information */}
          <div className="bg-white shadow-md rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">League Information</h2>

            <LeagueInfoFields
              name={leagueName}
              onNameChange={setLeagueName}
              icon={leagueIcon}
              onIconChange={setLeagueIcon}
              description={leagueDescription}
              onDescriptionChange={setLeagueDescription}
              isPublic={isPublic}
              onIsPublicChange={setIsPublic}
              publicDisabled={league?.isGlobal}
              publicNote={league?.isGlobal
                ? "The Global league is always public and cannot be changed."
                : undefined}
              publicWarning={league && !league.isGlobal && league.isPublic && !isPublic ? (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Changing this league to private means new players won't be able to join it when creating their profile. Existing players will remain in the league.
                  </p>
                </div>
              ) : undefined}
            />
          </div>

          {/* Player Management */}
          <div className="bg-white shadow-md rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Manage Players</h2>
            <p className="text-sm text-gray-600 mb-4">
              Add or remove players from this league. Players will maintain their league-specific ratings.
            </p>

            {allPlayers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">No players found.</p>
                <Link
                  to="/players"
                  className="text-primary-500 hover:text-primary-700 font-medium"
                >
                  Go to Players →
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-700">
                    Selected: {selectedPlayers.size} / {allPlayers.length} players
                  </span>
                  {(league.playerIds?.length || 0) !== selectedPlayers.size && (
                    <span className="ml-2 text-sm text-amber-600">
                      ({selectedPlayers.size - (league.playerIds?.length || 0) >= 0 ? '+' : ''}{selectedPlayers.size - (league.playerIds?.length || 0)} changes)
                    </span>
                  )}
                </div>
                <SimplePlayerSelectionGrid
                  players={allPlayers}
                  selectedPlayers={selectedPlayers}
                  onSelectionChange={setSelectedPlayers}
                />
              </>
            )}
          </div>

          {/* Current vs New Comparison */}
          {league.playerIds && (league.playerIds.length !== selectedPlayers.size || 
            !league.playerIds.every(id => selectedPlayers.has(id))) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Membership Changes</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-green-700 mb-2">Players Being Added</h4>
                  <div className="space-y-1">
                    {Array.from(selectedPlayers)
                      .filter(id => !league.playerIds?.includes(id))
                      .map(id => {
                        const player = allPlayers.find(p => p.id === id);
                        return player ? (
                          <div key={id} className="text-sm text-green-600">
                            + {player.name}
                          </div>
                        ) : null;
                      })}
                    {Array.from(selectedPlayers).filter(id => !league.playerIds?.includes(id)).length === 0 && (
                      <div className="text-sm text-gray-500">None</div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-red-700 mb-2">Players Being Removed</h4>
                  <div className="space-y-1">
                    {(league.playerIds || [])
                      .filter(id => !selectedPlayers.has(id))
                      .map(id => {
                        const player = allPlayers.find(p => p.id === id);
                        return player ? (
                          <div key={id} className="text-sm text-red-600">
                            - {player.name}
                          </div>
                        ) : null;
                      })}
                    {(league.playerIds || []).filter(id => !selectedPlayers.has(id)).length === 0 && (
                      <div className="text-sm text-gray-500">None</div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-xs text-amber-700">
                <strong>Note:</strong> Removed players will lose their ratings in this league. Added players will start with default ratings.
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4">
            <Link
              to={`/leagues/${league.id}`}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading || !leagueName.trim() || selectedPlayers.size === 0}
              className="px-6 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Updating...' : 'Update League'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeagueEditPage;